import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'

import { hasAuthCookie, hasRefreshCookie } from 'src/lib/utils/helpers'
import { refreshExpiredIdToken } from 'src/lib/utils/auth'
import { useUser } from 'src/lib/contexts/UserContext'
import { getJourneyStepFromPath } from 'src/lib/utils/navigation-rbac'
import useNavigation from 'src/lib/hooks/useNavigation'

export const RouteGuard = ({ children }: any) => {
  const router = useRouter()
  /**
   * Must start `false`: cookies aren't readable during prerender, so seeding from `hasAuthCookie()`
   * makes the server render nothing while the client renders the page — a hydration mismatch on every
   * protected route (this app is statically exported, so the shipped HTML is prerendered too).
   * The mount effect below runs `authCheck` and flips this immediately.
   */
  const [authorized, setAuthorized] = useState(false)

  const { user, mutateUser } = useUser()
  const {
    actions: { setJourneyStep }
  } = useNavigation()

  useEffect(() => {
    authCheck(router.asPath)

    const hideContent = () => setAuthorized(false)
    router.events.on('routeChangeStart', hideContent)

    const handleRouteChange = (url: string) => {
      authCheck(url)

      //Set the journey step based on the pathname on route change
      const pathname = router.pathname
      const journeyStep = getJourneyStepFromPath(pathname, user)
      setJourneyStep(journeyStep)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', hideContent)
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router, user, setJourneyStep])

  const authCheck = async (url: string) => {
    // redirect to login page if accessing a private page and not logged in
    if (!hasAuthCookie()) {
      if (hasRefreshCookie()) {
        await refreshExpiredIdToken()
        setAuthorized(true)
      } else {
        setAuthorized(false)
        // router.replace('/login')
      }
    } else {
      setAuthorized(true)
      if (!user) return
    }
  }

  return authorized && children
}
